<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <header class="header">
      <div class="header-left">
        <div class="brand">
          <img src="@/assets/logo.jpg" class="header-logo" alt="logo" />
          <h1 class="logo">极速飞驰管理系统</h1>
        </div>
      </div>
      <div class="header-right">
        <div class="breadcrumb">
          <span class="breadcrumb-item">{{ currentPage }}</span>
        </div>
        <div class="user-info">
          <span class="username">{{ userInfo ? userInfo.realName : '管理员' }}</span>
        </div>
      </div>
    </header>

    <div class="content-wrapper">
      <!-- 左侧边栏 -->
      <aside class="sidebar">
        <nav class="nav-menu">
          <ul class="menu-list">
            <li v-for="item in menuItems" :key="item.key" :class="['menu-item', {
              active: currentMenu === item.key,
              'has-submenu': item.submenu && item.submenu.length > 0,
              'submenu-open': currentMenu === item.key && item.submenu && item.submenu.length > 0
            }]" @click="handleMenuClick(item)">
              <div class="menu-item-content">
                <span>{{ item.name }}</span>
                <span v-if="item.submenu && item.submenu.length > 0" class="submenu-arrow">
                  {{ currentMenu === item.key ? '▼' : '▶' }}
                </span>
              </div>
              <!-- 二级菜单 -->
              <ul v-if="item.submenu && item.submenu.length > 0 && currentMenu === item.key" class="submenu-list">
                <li v-for="subItem in item.submenu" :key="subItem.key"
                  :class="['submenu-item', { active: currentSubMenu === subItem.key }]"
                  @click.stop="handleSubMenuClick(subItem)">
                  {{ subItem.name }}
                </li>
              </ul>
            </li>
          </ul>
          <div class="logout-section">
            <button class="logout-btn" @click="handleLogout">
              退出登录
            </button>
          </div>
        </nav>
      </aside>

      <!-- 主内容区域 -->
      <main class="main-content">
        <div class="content-area">
          <component :is="currentComponent" />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import StoreManagement from './StoreManagement.vue'
import DeviceManagement from './DeviceManagement.vue'
import DataQuery from './DataQuery.vue'
import AccountManagement from './AccountManagement.vue'
import GameManagement from './GameManagement.vue'
import AudioResourceManagement from './AudioResourceManagement.vue'
import RechargeRecords from './RechargeRecords.vue'
import ConsumptionRecords from './ConsumptionRecords.vue'
import StorePerformance from './StorePerformance.vue'
import OperationAuditLogs from './OperationAuditLogs.vue'
import RiskAlertCenter from './RiskAlertCenter.vue'
import StaffPermissionManagement from './StaffPermissionManagement.vue'
import MemberRuleManagement from './MemberRuleManagement.vue'
import CashierOrderAudit from './CashierOrderAudit.vue'
import CustomerAccountQuery from './CustomerAccountQuery.vue'

export default {
  name: 'MainLayout',
  components: {
    StoreManagement,
    DeviceManagement,
    DataQuery,
    AccountManagement,
    GameManagement,
    AudioResourceManagement,
    RechargeRecords,
    ConsumptionRecords,
    StorePerformance,
    OperationAuditLogs,
    RiskAlertCenter,
    StaffPermissionManagement,
    MemberRuleManagement,
    CashierOrderAudit,
    CustomerAccountQuery
  },
  props: {
    userInfo: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      currentMenu: 'store', // 默认选中门店管理
      currentSubMenu: null, // 当前选中的二级菜单
      menuItems: [
        { key: 'store', name: '多门店管理', component: 'StoreManagement' },
        { key: 'memberRule', name: '会员卡项规则', component: 'MemberRuleManagement' },
        { key: 'staffPermission', name: '店员权限管理', component: 'StaffPermissionManagement' },
        { key: 'device', name: '设备与服务资源', component: 'DeviceManagement' },
        {
          key: 'data',
          name: '收银订单与报表',
          component: 'DataQuery',
          submenu: [
            { key: 'cashierAudit', name: '收银订单审计', component: 'CashierOrderAudit' },
            { key: 'recharge', name: '充值记录', component: 'RechargeRecords' },
            { key: 'consumption', name: '消费记录', component: 'ConsumptionRecords' },
            { key: 'performance', name: '门店业绩统计', component: 'StorePerformance' },
            { key: 'risk', name: '异常预警中心', component: 'RiskAlertCenter' },
            { key: 'audit', name: '操作日志', component: 'OperationAuditLogs' }
          ]
        },
        {
          key: 'account',
          name: '账号与角色',
          component: 'AccountManagement',
          submenu: [
            { key: 'backendAccount', name: '后台账号管理', component: 'AccountManagement' },
            { key: 'customerAccount', name: '顾客账号查询', component: 'CustomerAccountQuery' }
          ]
        },
        { key: 'game', name: '游戏资源管理', component: 'GameManagement' },
        { key: 'audio', name: '提醒音频资源', component: 'AudioResourceManagement' }
      ]
    }
  },
  computed: {
    currentPage() {
      if (this.currentSubMenu) {
        const currentItem = this.menuItems.find(item => item.key === this.currentMenu)
        if (currentItem && currentItem.submenu) {
          const subItem = currentItem.submenu.find(sub => sub.key === this.currentSubMenu)
          return subItem ? subItem.name : '首页'
        }
      }
      const currentItem = this.menuItems.find(item => item.key === this.currentMenu)
      return currentItem ? currentItem.name : '首页'
    },
    currentComponent() {
      if (this.currentSubMenu) {
        const currentItem = this.menuItems.find(item => item.key === this.currentMenu)
        if (currentItem && currentItem.submenu) {
          const subItem = currentItem.submenu.find(sub => sub.key === this.currentSubMenu)
          return subItem ? subItem.component : 'StoreManagement'
        }
      }
      const currentItem = this.menuItems.find(item => item.key === this.currentMenu)
      return currentItem ? currentItem.component : 'StoreManagement'
    }
  },
  methods: {
    handleMenuClick(item) {
      if (item.submenu && item.submenu.length > 0) {
        // 如果有二级菜单，切换展开状态
        if (this.currentMenu === item.key) {
          // 如果当前菜单已展开，则关闭
          this.currentMenu = 'store'
          this.currentSubMenu = null
        } else {
          // 展开新菜单，默认跳转到该分组的第一个页面
          this.currentMenu = item.key
          this.currentSubMenu = item.submenu[0].key
        }
      } else {
        // 没有二级菜单，直接切换
        this.currentMenu = item.key
        this.currentSubMenu = null
      }
    },
    handleSubMenuClick(subItem) {
      this.currentSubMenu = subItem.key
    },
    handleLogout() {
      if (confirm('确定要退出登录吗？')) {
        // 这里可以添加退出登录的逻辑
        this.$emit('logout')
      }
    }
  }
}
</script>

<style scoped>
.main-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;
}

.header {
  height: 60px;
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-left .brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 6px;
  border: 1px solid #eee;
}

.header-left .logo {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  letter-spacing: 0.5px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.breadcrumb {
  color: #666;
  font-size: 14px;
}

.breadcrumb-item {
  color: #1890ff;
}

.user-info {
  color: #333;
  font-size: 14px;
}

.content-wrapper {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.sidebar {
  width: 200px;
  background-color: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
}

.nav-menu {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 0;
}

.menu-list {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1;
}

.menu-item {
  cursor: pointer;
  border: 1px solid transparent;
  margin: 4px 16px;
  border-radius: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  color: #333;
}

.menu-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
}

.menu-item:hover {
  background-color: #f0f0f0;
  border-color: #d9d9d9;
}

.menu-item.active {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
  font-weight: 500;
}

.menu-item.has-submenu .submenu-arrow {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.menu-item.submenu-open .submenu-arrow {
  transform: rotate(0deg);
}

.submenu-list {
  list-style: none;
  margin: 0;
  padding: 0;
  background-color: #f8f9fa;
  border-radius: 4px;
  margin-top: 4px;
}

.submenu-item {
  padding: 8px 32px;
  cursor: pointer;
  font-size: 13px;
  color: #666;
  transition: all 0.3s ease;
  border-radius: 4px;
  margin: 2px 8px;
}

.submenu-item:hover {
  background-color: #e6f7ff;
  color: #1890ff;
}

.submenu-item.active {
  background-color: #1890ff;
  color: white;
  font-weight: 500;
}

.logout-section {
  padding: 16px;
  border-top: 1px solid #e8e8e8;
}

.logout-btn {
  width: 100%;
  padding: 8px 16px;
  background-color: #ff4d4f;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.logout-btn:hover {
  background-color: #ff7875;
}

.main-content {
  flex: 1;
  background-color: #f5f5f5;
  overflow: auto;
}

.content-area {
  padding: 24px;
  min-height: 100%;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .sidebar {
    width: 160px;
  }

  .menu-item {
    padding: 10px 16px;
    margin: 2px 8px;
    font-size: 13px;
  }

  .header {
    padding: 0 16px;
  }

  .content-area {
    padding: 16px;
  }
}
</style>
